import {
  ExperienceAPI,
  FileUploadResponse,
  SQLExecutionResponse,
} from "./experienceAPI";

export interface PreviewResponse {
  file_id: string;
  data: Array<{ [key: string]: any }>;
  columns: string[];
  column_types: { [key: string]: string };
  total_rows: number;
  page: number;
  limit: number;
  success: boolean;
}

export interface Insight {
  id: string;
  title: string;
  description: string;
  confidence: number;
  relevance: number;
  category: string;
  related_columns: string[];
  sql_query: string;
  visualization_type: string;
  visualization_config: { [key: string]: any };
}

export interface InsightsResponse {
  file_id: string;
  insights: Insight[];
  status: string;
  message: string;
  success: boolean;
}

export interface Chart {
  id: string;
  title: string;
  chart_type: string;
  description: string;
  sql_query: string;
  columns: string[];
  x_axis: string;
  y_axis: string[];
  config: { [key: string]: any };
  position: { [key: string]: number };
}

export interface Dashboard {
  id: string;
  title: string;
  description: string;
  charts: Chart[];
  layout: { [key: string]: any };
}

export interface DashboardsResponse {
  file_id: string;
  dashboards: Dashboard[];
  status: string;
  message: string;
  success: boolean;
}

export interface NextStep {
  id: string;
  title: string;
  description: string;
  action_type: string;
  importance: number;
  context: { [key: string]: any };
  suggested_query: string;
}

export interface NextStepsResponse {
  file_id: string;
  next_steps: NextStep[];
  status: string;
  message: string;
  success: boolean;
}

export interface AnalysisResult {
  fileId: string;
  previewData: PreviewResponse;
  metadata: FileUploadResponse["metadata"] | null;
  insights: Insight[];
  dashboards: Dashboard[];
  nextSteps: NextStep[];
  insightsWithData: Array<{
    insight: Insight;
    data: any[];
  }>;
  dashboardsWithData: Array<{
    chart: Chart;
    data: any[];
  }>;
}

export interface AnalysisProgress {
  step:
    | "preview"
    | "insights"
    | "dashboards"
    | "next-steps"
    | "executing-sql"
    | "complete";
  progress: number;
  message: string;
}

export class DataAnalysisService {
  private static fileIdStorage: Map<string, string> = new Map();

  static storeFileId(sessionKey: string, fileId: string): void {
    this.fileIdStorage.set(sessionKey, fileId);
    sessionStorage.setItem(`file_id_${sessionKey}`, fileId);
  }

  static getFileId(sessionKey: string): string | null {
    const memoryId = this.fileIdStorage.get(sessionKey);
    if (memoryId) return memoryId;

    const sessionId = sessionStorage.getItem(`file_id_${sessionKey}`);
    if (sessionId) {
      this.fileIdStorage.set(sessionKey, sessionId);
      return sessionId;
    }

    return null;
  }

  static clearFileId(sessionKey: string): void {
    this.fileIdStorage.delete(sessionKey);
    sessionStorage.removeItem(`file_id_${sessionKey}`);
  }

  static async analyzeFile(
    file_id: string,
    sessionKey: string,
    onProgress?: (progress: AnalysisProgress) => void,
    onStepUpdate?: (step: string, status: string, data?: any) => void
  ): Promise<AnalysisResult> {
    try {
      this.storeFileId(sessionKey, file_id);

      onProgress?.({
        step: "preview",
        progress: 20,
        message: "Creating preview...",
      });

      // console.log("🔍 Starting preview step for file_id:", file_id);
      onStepUpdate?.("preview", "processing");
      const previewResponse = await this.pollForCompletion(
        async () => {
          // console.log("📡 Calling ExperienceAPI.getPreview...");
          const response = await ExperienceAPI.getPreview(file_id);
          // console.log("📡 Preview response:", response);
          return response;
        },
        "preview",
        onStepUpdate
      );
      if (!previewResponse.success) {
        throw new Error("Failed to generate preview");
      }

      onProgress?.({
        step: "insights",
        progress: 40,
        message: "Generating AI insights...",
      });

      // console.log("🔍 Starting insights step for file_id:", file_id);
      onStepUpdate?.("insights", "processing");
      const insightsResponse = await this.pollForCompletion(
        async () => {
          // console.log("📡 Calling ExperienceAPI.getInsights...");
          const response = await ExperienceAPI.getInsights(file_id);
          // console.log("📡 Insights response:", response);
          return response;
        },
        "insights",
        onStepUpdate
      );
      if (!insightsResponse.success) {
        throw new Error("Failed to generate insights");
      }

      onProgress?.({
        step: "dashboards",
        progress: 60,
        message: "Creating dashboards...",
      });

      onStepUpdate?.("dashboards", "processing");
      const dashboardsResponse = await this.pollForCompletion(
        () => ExperienceAPI.getDashboards(file_id),
        "dashboards",
        onStepUpdate
      );
      if (!dashboardsResponse.success) {
        throw new Error("Failed to generate dashboards");
      }

      onProgress?.({
        step: "next-steps",
        progress: 80,
        message: "Generating next steps...",
      });

      onStepUpdate?.("next-steps", "processing");
      const nextStepsResponse = await this.pollForCompletion(
        () => ExperienceAPI.getNextSteps(file_id),
        "next-steps",
        onStepUpdate
      );
      if (!nextStepsResponse.success) {
        throw new Error("Failed to generate next steps");
      }

      const insightsWithData = insightsResponse.insights?.map((insight) => ({
        insight: insight as unknown as Insight,
        data: [],
      }));

      const dashboardsWithData =
        (dashboardsResponse as any).dashboards?.flatMap(
          (dashboard: any) =>
            dashboard.charts?.map((chart: any) => ({
              chart: chart as unknown as Chart,
              data: [],
            })) || []
        ) || [];

      onProgress?.({
        step: "complete",
        progress: 100,
        message: "Analysis complete!",
      });

      onStepUpdate?.("complete", "completed");

      return {
        fileId: file_id,
        metadata: null,
        previewData: previewResponse as PreviewResponse,
        insights: insightsResponse.insights as unknown as Insight[],
        dashboards: (dashboardsResponse as any)
          .dashboards as unknown as Dashboard[],
        nextSteps: nextStepsResponse.next_steps as unknown as NextStep[],
        insightsWithData,
        dashboardsWithData,
      };
    } catch (error) {
      console.error("Data analysis failed:", error);
      onStepUpdate?.("error", "failed", { error: error.message });
      throw error;
    }
  }

  private static async pollForCompletion<T>(
    apiCall: () => Promise<T>,
    stepName: string,
    onStepUpdate?: (step: string, status: string, data?: any) => void,
    maxAttempts: number = 60
  ): Promise<T> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await apiCall();

        if (response && typeof response === "object" && "status" in response) {
          const status = (response as any).status;

          if (status === "completed") {
            onStepUpdate?.(stepName, "completed", response);
            return response;
          } else if (status === "failed") {
            onStepUpdate?.(stepName, "failed", response);
            throw new Error(`Step ${stepName} failed`);
          } else if (status === "processing") {
            onStepUpdate?.(stepName, "processing", response);
            await new Promise((resolve) => setTimeout(resolve, 2000));
            attempts++;
            continue;
          }
        }

        onStepUpdate?.(stepName, "completed", response);
        return response;
      } catch (error) {
        console.error(`Error polling ${stepName}:`, error);
        onStepUpdate?.(stepName, "failed", { error: error.message });
        throw error;
      }
    }

    onStepUpdate?.(stepName, "timeout", { error: "Operation timed out" });
    throw new Error(`Step ${stepName} timed out after ${maxAttempts} attempts`);
  }

  // static async getAnalysisForExistingFile(
  //   sessionKey: string,
  //   onProgress?: (progress: AnalysisProgress) => void
  // ): Promise<AnalysisResult | null> {
  //   const fileId = this.getFileId(sessionKey);
  //   if (!fileId) {
  //     return null;
  //   }

  //   try {
  //     onProgress?.({
  //       step: "preview",
  //       progress: 10,
  //       message: "Loading preview data...",
  //     });

  //     const previewResponse = await ExperienceAPI.getPreview(fileId);

  //     onProgress?.({
  //       step: "insights",
  //       progress: 20,
  //       message: "Loading AI insights...",
  //     });

  //     const insightsResponse = await ExperienceAPI.getInsights(fileId);

  //     onProgress?.({
  //       step: "dashboards",
  //       progress: 40,
  //       message: "Loading dashboards...",
  //     });

  //     const dashboardsResponse = await ExperienceAPI.getDashboards(fileId);

  //     onProgress?.({
  //       step: "next-steps",
  //       progress: 60,
  //       message: "Loading next steps...",
  //     });

  //     const nextStepsResponse = await ExperienceAPI.getNextSteps(fileId);

  //     onProgress?.({
  //       step: "executing-sql",
  //       progress: 80,
  //       message: "Loading visualization data...",
  //     });

  //     const insightsWithData = await Promise.all(
  //       insightsResponse.insights.map(async (insight) => {
  //         try {
  //           const dataResponse = await ExperienceAPI.executeSQL(
  //             fileId,
  //             insight.sql_query
  //           );
  //           return {
  //             insight,
  //             data: dataResponse.success ? dataResponse.results : [],
  //           };
  //         } catch (error) {
  //           console.error(
  //             `Failed to execute SQL for insight ${insight.id}:`,
  //             error
  //           );
  //           return {
  //             insight,
  //             data: [],
  //           };
  //         }
  //       })
  //     );

  //     const dashboardsWithData = await Promise.all(
  //       dashboardsResponse.charts.map(async (chart) => {
  //         try {
  //           const dataResponse = await ExperienceAPI.executeSQL(
  //             fileId,
  //             chart.sql_query
  //           );
  //           return {
  //             chart,
  //             data: dataResponse.success ? dataResponse.results : [],
  //           };
  //         } catch (error) {
  //           console.error(
  //             `Failed to execute SQL for chart ${chart.id}:`,
  //             error
  //           );
  //           return {
  //             chart,
  //             data: [],
  //           };
  //         }
  //       })
  //     );

  //     onProgress?.({
  //       step: "complete",
  //       progress: 100,
  //       message: "Analysis loaded!",
  //     });

  //     return {
  //       fileId,
  //       previewData: previewResponse.data,
  //       metadata: {
  //         filename: "Previously uploaded file",
  //         file_size: 0,
  //         row_count: 0,
  //         column_count: 0,
  //         columns: [],
  //         column_types: {},
  //         created_at: new Date().toISOString(),
  //         updated_at: new Date().toISOString(),
  //       },
  //       insights: insightsResponse.insights,
  //       dashboards: dashboardsResponse.charts,
  //       nextSteps: nextStepsResponse.next_steps,
  //       insightsWithData,
  //       dashboardsWithData,
  //     };
  //   } catch (error) {
  //     console.error("Failed to load analysis for existing file:", error);
  //     return null;
  //   }
  // }

  static async executeCustomSQL(
    fileId: string,
    sqlQuery: string
  ): Promise<SQLExecutionResponse> {
    return await ExperienceAPI.executeSQL(fileId, sqlQuery);
  }

  static async handleNextStepAction(fileId: string, step: any): Promise<any> {
    switch (step.action_type) {
      case "drill_down":
        return await this.handleDrillDown(fileId, step.context);

      case "correlation_analysis":
        return await this.handleCorrelationAnalysis(fileId, step.context);

      case "filter":
        return await this.handleFilter(fileId, step.context);

      case "export":
        return await this.handleExport(fileId, step.context);

      default:
        throw new Error(`Unknown action type: ${step.action_type}`);
    }
  }

  private static async handleDrillDown(
    fileId: string,
    context: any
  ): Promise<any> {
    const { dimension, filters } = context;
    const sqlQuery = `SELECT ${dimension}, COUNT(*) as count FROM data WHERE ${Object.entries(
      filters
    )
      .map(([key, value]) => `${key} = '${value}'`)
      .join(" AND ")} GROUP BY ${dimension}`;
    return await this.executeCustomSQL(fileId, sqlQuery);
  }

  private static async handleCorrelationAnalysis(
    fileId: string,
    context: any
  ): Promise<any> {
    const { variables } = context;
    const sqlQuery = `SELECT ${variables.join(", ")} FROM data LIMIT 1000`;
    return await this.executeCustomSQL(fileId, sqlQuery);
  }

  private static async handleFilter(
    fileId: string,
    context: any
  ): Promise<any> {
    const { filters } = context;
    const whereClause = Object.entries(filters)
      .map(([key, value]) => `${key} = '${value}'`)
      .join(" AND ");
    const sqlQuery = `SELECT * FROM data WHERE ${whereClause}`;
    return await this.executeCustomSQL(fileId, sqlQuery);
  }

  private static async handleExport(
    fileId: string,
    context: any
  ): Promise<any> {
    return { success: true, message: "Export initiated" };
  }
}
